import {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { BadgeV2 } from "./badges/Badge";
import { Dropdown } from "@lockerpm/design";
import { LoaderV2 } from "./LoadingState";
import { DropdownRenderV2 } from "./antdHelpers/DropdownRender";
import { DropdownItemRenderV2 } from "./antdHelpers/DropdownItemRender";

// Define a Tag object type
export type Tag = {
  id: string;
  name: string;
  extraData?: Record<string, any>;
  badgeOptions?: {
    defaultIcon?: boolean;
    noColor?: boolean;
    icon?: ReactNode;
  };
  dropdownOptions?: {
    icon?: ReactNode;
  };
};

// Define ref type with methods that can be called by parent
export interface TagsInputWithSearchRef {
  clearSearchValue: () => void;
}

type Props = {
  id?: string;
  tags: Tag[];
  setTags: (tags: Tag[]) => void;
  searchFunction: (query: string) => Promise<Tag[]>;
  disabled?: boolean;
  placeholder?: string;
  emptyState?: React.ReactNode;
  inputValidate?: (value: string) => { isValid: boolean; message?: string };
};

const TagsInputWithSearchWithRef = forwardRef<TagsInputWithSearchRef, Props>(
  (props, ref) => {
    const {
      id,
      tags,
      setTags,
      searchFunction,
      disabled,
      placeholder = "",
      emptyState,
      inputValidate,
    } = props;

    const syncWidthId = "tags-input-" + id;

    // ----------------------------- STATE -----------------------------

    const [focused, setFocused] = useState<boolean>(false);

    const [searchValue, setSearchValue] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<Tag[]>([]);
    const [selectedResultIndex, setSelectedResultIndex] = useState<
      string | null
    >(null);

    // ----------------------------- REFS -----------------------------

    const inputRef = useRef<HTMLInputElement>(null);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const isKeyboardNavigationRef = useRef<boolean>(false);

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      clearSearchValue: () => {
        setSearchValue("");
        setSearchResults([]);
        setSelectedResultIndex(null);
      },
    }));

    // ----------------------------- EFFECTS -----------------------------

    useEffect(() => {
      // Clean up the debounce timeout on unmount
      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }, []);

    useEffect(() => {
      // Skip search if change was due to keyboard navigation
      if (isKeyboardNavigationRef.current) {
        isKeyboardNavigationRef.current = false;
        return;
      }

      // Validate input if validator is provided
      if (inputValidate) {
        const validation = inputValidate(searchValue);
        if (!validation.isValid) {
          // Don't search if input is invalid
          return;
        }
      }

      // Debounce the search to avoid making too many requests
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      searchDebounceRef.current = setTimeout(async () => {
        try {
          setIsSearching(true);
          const results = await searchFunction(searchValue);

          if (results === null) return;

          // Filter out tags that are already selected
          const filteredResults = results.filter(
            (result) => !tags.some((tag) => tag.id === result.id)
          );

          setSearchResults(filteredResults);
          setSelectedResultIndex(null); // Reset selection index when results change
        } catch (error) {
          console.error("Error searching for tags:", error);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    }, [searchValue, tags, searchFunction, inputValidate]);

    // ----------------------------- METHODS -----------------------------

    const removeTag = (tagId: string) => {
      if (disabled) return;
      setTags(tags.filter((tag) => tag.id !== tagId));
    };

    const addTag = (tag: Tag) => {
      if (disabled) return;

      // Validate input if validator is provided before adding tag
      if (inputValidate) {
        const validation = inputValidate(tag.name);
        if (!validation.isValid) {
          // Don't add tag if input is invalid
          return;
        }
      }

      setTags([...tags, tag]);
      // After adding, reset search results
      setSearchValue("");
      setSearchResults([]);
      setSelectedResultIndex(null);
      inputRef.current?.focus();
    };

    /** "direction = 1" to move focus to next item, "direction = -1" to move focus to previous item. */
    const shiftFocusResult = (direction: number) => {
      if (searchResults.length > 0) {
        const currentSelectedResultIndex = searchResults.findIndex(
          (item) => item.id === selectedResultIndex
        );
        // Select the next result in the list
        const nextIndex =
          (currentSelectedResultIndex + searchResults.length + direction) %
          searchResults.length;
        setSelectedResultIndex(searchResults[nextIndex].id);
        // Update input value without triggering search
        isKeyboardNavigationRef.current = true;
        setSearchValue(searchResults[nextIndex].name);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (searchResults.length > 0 && selectedResultIndex !== null) {
          // Validate input if validator is provided before adding tag
          if (inputValidate) {
            const validation = inputValidate(searchValue);
            if (!validation.isValid) {
              // Don't add tag if input is invalid
              return;
            }
          }
          const currentSelectedResult = searchResults.find(
            (item) => item.id === selectedResultIndex
          );
          if (currentSelectedResult) addTag(currentSelectedResult);
        }
      } else if (!e.shiftKey && e.key === "Tab") {
        e.preventDefault();
        shiftFocusResult(1);
      } else if (e.shiftKey && e.key === "Tab") {
        e.preventDefault();
        shiftFocusResult(-1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        shiftFocusResult(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        shiftFocusResult(-1);
      } else if (e.key === "Escape") {
        setFocused(false);
      }
    };

    // ----------------------------- RENDER -----------------------------

    return (
      <Dropdown
        menu={{
          items: searchResults.map((item) => ({
            key: item.id,
            label: (
              <DropdownItemRenderV2
                label={
                  <>
                    {item.dropdownOptions && item.dropdownOptions.icon
                      ? item.dropdownOptions.icon
                      : null}
                    {item.name}
                  </>
                }
                isSelected={false}
                isFocused={item.id === selectedResultIndex}
              />
            ),
          })),
          onClick: ({ key }) => {
            const selected = searchResults.find((item) => item.id === key);
            if (selected) {
              addTag(selected);
            }
          },
          onMouseDown: (e) => {
            // Context: both MouseDown and Click will trigger Blur event.
            // We need Blur event to happen AFTER Click event, since we want users to be able to click into dropdown's items.
            // Blurring before clicking will make the dropdown disappear right before click event being triggered.
            // So this preventDefault is to prevent MouseDown triggering Blur.
            e.preventDefault();
          },
        }}
        dropdownRender={(menu) => (
          <DropdownRenderV2 syncWidthId={syncWidthId}>
            {isSearching ? (
              <div className="flex justify-center items-center">
                <LoaderV2 baseSize={2} />
              </div>
            ) : searchResults.length > 0 ? (
              menu
            ) : (
              <>
                {emptyState || (
                  <div className="text-gray-v2-600 text-center">No data</div>
                )}
              </>
            )}
          </DropdownRenderV2>
        )}
        trigger={["click"]}
        open={focused}
      >
        <div
          id={syncWidthId}
          style={{
            paddingTop: tags.length > 0 ? undefined : "0.5rem",
            paddingBottom: tags.length > 0 ? undefined : "0.5rem",
          }}
          className={`tags-input ${disabled ? "bg-gray-v2-50 opacity-50" : ""}`}
          onClick={(e) => {
            // If the clicked element is not an input or tag, focus the input
            if (e.target === e.currentTarget) {
              inputRef.current?.focus();
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
        >
          {tags.map((tag) => (
            <BadgeV2
              key={tag.id}
              name={tag.name}
              onClose={() => {
                removeTag(tag.id);
              }}
              defaultIcon={tag.badgeOptions?.defaultIcon}
              color={tag.badgeOptions?.noColor ? "gray" : undefined}
              leftIcon={tag.badgeOptions?.icon}
            />
          ))}

          <input
            type="text"
            disabled={disabled}
            ref={inputRef}
            style={{
              paddingLeft: tags.length > 0 ? undefined : "0",
            }}
            value={searchValue}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchValue(newValue);
            }}
            placeholder={placeholder}
          />
        </div>
      </Dropdown>
    );
  }
);

TagsInputWithSearchWithRef.displayName = "TagsInputWithSearch";

export const TagsInputWithSearch = TagsInputWithSearchWithRef;
